import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import GetAccessModal from '@/components/landing/GetAccessModal';
import { ThemeContext } from '@/contexts/ThemeContext';
import { tLanding, type LandingLang, type LandingTranslationKey } from '@/i18n/landingTranslations';
import { LandingFooter } from '@/components/landingPrototype/LandingFooter';
import { FaqSidebar } from '@/components/landingPrototype/FaqSidebar';
import { LandingTopNav } from '@/components/landingPrototype/LandingTopNav';
import { LandingSeo } from '@/components/seo/LandingSeo';
import { highlightLandingText } from '@/components/landingPrototype/highlightLandingText';

import '@/styles/landingPrototype.css';

const GpuRentalLandingPage: React.FC = () => {
  const { isDark, setIsDark } = useContext(ThemeContext);
  const motionEnabled = useMemo(() => {
    try {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
      return !reduce && !coarse;
    } catch {
      return true;
    }
  }, []);

  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [isFaqCollapsed, setIsFaqCollapsed] = useState(true);
  const heroRef = useRef<HTMLElement | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 500, y: 500 });

  const whySectionRef = useRef<HTMLElement | null>(null);
  const [whyMousePosition, setWhyMousePosition] = useState({ x: 500, y: 500 });
  const tasksSectionRef = useRef<HTMLElement | null>(null);
  const [tasksVisible, setTasksVisible] = useState(false);
  const taskCardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [whyVisible, setWhyVisible] = useState(false);
  const whyItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const whyDividerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [language, setLanguage] = useState<'EN' | 'RU'>(() => {
    try {
      const stored = (localStorage.getItem('language') || 'en').toLowerCase();
      return stored === 'ru' ? 'RU' : 'EN';
    } catch {
      return 'EN';
    }
  });

  const lang: LandingLang = language === 'RU' ? 'ru' : 'en';
  const t = (key: LandingTranslationKey) => tLanding(lang, key);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  useEffect(() => {
    const el = tasksSectionRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          setTasksVisible(true);
          taskCardRefs.current.forEach((cardRef, index) => {
            if (!cardRef) return;
            window.setTimeout(() => {
              cardRef.classList.add('visible');
            }, index * 150);
          });
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = whySectionRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          setWhyVisible(true);
          whyItemRefs.current.forEach((itemRef, index) => {
            if (!itemRef) return;
            window.setTimeout(() => itemRef.classList.add('visible'), index * 150);
          });
          whyDividerRefs.current.forEach((divRef, index) => {
            if (!divRef) return;
            window.setTimeout(() => divRef.classList.add('visible'), (index + 1) * 150);
          });
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const scale = Math.max(rect.width / 1000, rect.height / 1000);
      const leftEdge = (rect.width - scale * 1000) / 2;
      const topEdge = (rect.height - scale * 1000) / 2;
      let svgX = (e.clientX - rect.left - leftEdge) / scale;
      let svgY = (e.clientY - rect.top - topEdge) / scale;
      svgX = Math.max(0, Math.min(1000, svgX));
      svgY = Math.max(0, Math.min(1000, svgY));
      setMousePosition({ x: svgX, y: svgY });
    };

    const handleMouseLeave = () => {
      setMousePosition({ x: 500, y: 500 });
    };

    const heroEl = heroRef.current;
    if (!heroEl) return undefined;

    heroEl.addEventListener('mousemove', handleMouseMove, { passive: true });
    heroEl.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      heroEl.removeEventListener('mousemove', handleMouseMove);
      heroEl.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [motionEnabled]);

  const HERO_TRI_POSITIONS = [
    { x: 100, y: 120 }, { x: 350, y: 80 }, { x: 600, y: 150 }, { x: 850, y: 100 },
    { x: 180, y: 380 }, { x: 450, y: 420 }, { x: 720, y: 350 }, { x: 950, y: 400 },
    { x: 120, y: 650 }, { x: 400, y: 680 }, { x: 680, y: 620 }, { x: 880, y: 700 },
  ];
  const heroDisplayRef = useRef<number[]>(Array(12).fill(0));
  const maxDegPerFrame = 0.6;
  const heroLookAt = useMemo(() => {
    const mouseSvgX = mousePosition.x;
    const mouseSvgY = mousePosition.y;
    const prev = heroDisplayRef.current;
    const next = prev.slice(0, 12);
    for (let i = 0; i < 12; i++) {
      const pos = HERO_TRI_POSITIONS[i] ?? { x: 500, y: 500 };
      const targetAngle = Math.atan2(mouseSvgY - pos.y, mouseSvgX - pos.x) * (180 / Math.PI) + 90;
      let error = targetAngle - prev[i];
      while (error > 180) error -= 360;
      while (error < -180) error += 360;
      const step = Math.sign(error) * Math.min(Math.abs(error), maxDegPerFrame);
      let displayed = prev[i] + step;
      if (displayed > 360 || displayed < -360) displayed = ((displayed % 360) + 360) % 360;
      next[i] = displayed;
    }
    heroDisplayRef.current = next;
    return next;
  }, [mousePosition.x, mousePosition.y]);
  const getTriangleRotation = (index: number) => heroLookAt[index] ?? 0;

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const handleWhyMouseMove = (e: MouseEvent) => {
      if (!whySectionRef.current) return;
      const rect = whySectionRef.current.getBoundingClientRect();
      const scale = Math.max(rect.width / 1000, rect.height / 1000);
      const leftEdge = (rect.width - scale * 1000) / 2;
      const topEdge = (rect.height - scale * 1000) / 2;
      let svgX = (e.clientX - rect.left - leftEdge) / scale;
      let svgY = (e.clientY - rect.top - topEdge) / scale;
      svgX = Math.max(0, Math.min(1000, svgX));
      svgY = Math.max(0, Math.min(1000, svgY));
      setWhyMousePosition({ x: svgX, y: svgY });
    };

    const handleWhyMouseLeave = () => {
      setWhyMousePosition({ x: 500, y: 500 });
    };

    const el = whySectionRef.current;
    if (!el) return undefined;
    el.addEventListener('mousemove', handleWhyMouseMove, { passive: true });
    el.addEventListener('mouseleave', handleWhyMouseLeave);
    return () => {
      el.removeEventListener('mousemove', handleWhyMouseMove);
      el.removeEventListener('mouseleave', handleWhyMouseLeave);
    };
  }, [motionEnabled]);

  const WHY_TRI_POSITIONS = [
    { x: 200, y: 150 }, { x: 750, y: 200 },
    { x: 150, y: 500 }, { x: 800, y: 550 },
    { x: 300, y: 800 }, { x: 700, y: 750 },
  ];
  const whyDisplayRef = useRef<number[]>(Array(6).fill(0));
  const whyLookAt = useMemo(() => {
    const mouseSvgX = whyMousePosition.x;
    const mouseSvgY = whyMousePosition.y;
    const prev = whyDisplayRef.current;
    const next = prev.slice(0, 6);
    for (let i = 0; i < 6; i++) {
      const pos = WHY_TRI_POSITIONS[i] ?? { x: 500, y: 500 };
      const targetAngle = Math.atan2(mouseSvgY - pos.y, mouseSvgX - pos.x) * (180 / Math.PI) + 90;
      let error = targetAngle - prev[i];
      while (error > 180) error -= 360;
      while (error < -180) error += 360;
      const step = Math.sign(error) * Math.min(Math.abs(error), maxDegPerFrame);
      let displayed = prev[i] + step;
      if (displayed > 360 || displayed < -360) displayed = ((displayed % 360) + 360) % 360;
      next[i] = displayed;
    }
    whyDisplayRef.current = next;
    return next;
  }, [whyMousePosition.x, whyMousePosition.y]);
  const getWhyTriangleRotation = (index: number) => whyLookAt[index] ?? 0;

  const toggleLanguage = () => {
    const next = language === 'EN' ? 'RU' : 'EN';
    setLanguage(next);
    try {
      localStorage.setItem('language', next.toLowerCase());
    } catch {
      // ignore
    }
  };

  return (
    <div
      className={`swarmLandingRoot ${isDark ? '' : 'light-theme'}`}
      style={
        {
          ['--faq-offset' as any]: isFaqCollapsed ? '64px' : '360px',
          ['--page-accent' as any]: '#ff6384',
          ['--page-accent-dark' as any]: '#d04d5d',
        } as React.CSSProperties
      }
    >
      <FaqSidebar t={t} accentColor="#ff6384" onCollapsedChange={setIsFaqCollapsed} />
      <LandingSeo
        title={t('gpuRentalTitle')}
        description={t('gpuRentalSubtitle')}
        path="/gpu-rental"
        lang={lang}
        breadcrumbs={[
          { name: '🏠 Home', path: '/' },
          { name: '🖥️ GPU rental', path: '/gpu-rental' },
        ]}
      />
      <LandingTopNav t={t} />

      <div className="landing-main">
      <div className="page page-gpu-rental">
        <section className="hero" ref={heroRef}>
          <div className="hero-background">
            <svg
              className="triangle-grid"
              viewBox="0 0 1000 1000"
              preserveAspectRatio="xMidYMid slice"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="triGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(93, 99, 255, 0.5)" />
                  <stop offset="100%" stopColor="rgba(138, 43, 226, 0.4)" />
                </linearGradient>
                <linearGradient id="triGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(138, 43, 226, 0.45)" />
                  <stop offset="100%" stopColor="rgba(75, 192, 192, 0.35)" />
                </linearGradient>
                <linearGradient id="triGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(75, 192, 192, 0.4)" />
                  <stop offset="100%" stopColor="rgba(255, 99, 132, 0.3)" />
                </linearGradient>
                <filter id="triGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {Array.from({ length: 12 }).map((_, i) => {
                const pos = HERO_TRI_POSITIONS[i] ?? { x: 150 + (i % 4) * 250, y: 150 + Math.floor(i / 4) * 300 };
                const size = 70 + (i % 4) * 25 + (i % 5) * 15;
                const totalRotation = getTriangleRotation(i);
                return (
                  <g key={i} transform={`translate(${pos.x}, ${pos.y}) rotate(${totalRotation})`}>
                    <polygon
                      points={`0,${-size} ${size * 0.866},${size * 0.5} ${-size * 0.866},${size * 0.5}`}
                      fill="none"
                      stroke={`url(#triGradient${(i % 3) + 1})`}
                      strokeWidth="1.5"
                      filter="url(#triGlow)"
                      opacity="0.65"
                    />
                  </g>
                );
              })}
            </svg>
          </div>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="hero-content">
              <div className="hero-text">
                <h1>{highlightLandingText(t('gpuRentalTitle'), ['GPU', 'Rental', 'rent', 'сервер', 'аренда'])}</h1>
                <h2>{t('gpuRentalSubtitle')}</h2>
                <div className="hero-buttons">
                  <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                    {t('getAccess')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="gpu-rental-tasks" ref={tasksSectionRef}>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <h2 className={`gpu-rental-tasks-title ${tasksVisible ? 'visible' : ''}`}>
              {highlightLandingText(t('gpuRentalTasksTitle'), ['GPU', 'servers', 'сервера'])}
            </h2>
            <div className="gpu-rental-tasks-grid">
              <div className="gpu-rental-task-card" ref={el => { taskCardRefs.current[0] = el; }}>
                <h3 className="gpu-rental-task-title">{t('gpuRentalTask1Title')}</h3>
                <p className="gpu-rental-task-desc">{t('gpuRentalTask1Desc')}</p>
              </div>
              <div className="gpu-rental-task-card" ref={el => { taskCardRefs.current[1] = el; }}>
                <h3 className="gpu-rental-task-title">{t('gpuRentalTask2Title')}</h3>
                <p className="gpu-rental-task-desc">{t('gpuRentalTask2Desc')}</p>
              </div>
              <div className="gpu-rental-task-card" ref={el => { taskCardRefs.current[2] = el; }}>
                <h3 className="gpu-rental-task-title">{t('gpuRentalTask3Title')}</h3>
                <p className="gpu-rental-task-desc">{t('gpuRentalTask3Desc')}</p>
              </div>
              <div className="gpu-rental-task-card" ref={el => { taskCardRefs.current[3] = el; }}>
                <h3 className="gpu-rental-task-title">{t('gpuRentalTask4Title')}</h3>
                <p className="gpu-rental-task-desc">{t('gpuRentalTask4Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="gpu-rental-why" style={{ position: 'relative', overflow: 'hidden' }} ref={whySectionRef}>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }} aria-hidden="true">
            <svg
              viewBox="0 0 1000 1000"
              preserveAspectRatio="xMidYMid slice"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.75 }}
            >
              <defs>
                <linearGradient id="whyTriGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(93, 99, 255, 0.4)" />
                  <stop offset="100%" stopColor="rgba(138, 43, 226, 0.3)" />
                </linearGradient>
                <linearGradient id="whyTriGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(138, 43, 226, 0.35)" />
                  <stop offset="100%" stopColor="rgba(75, 192, 192, 0.25)" />
                </linearGradient>
                <linearGradient id="whyTriGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(75, 192, 192, 0.3)" />
                  <stop offset="100%" stopColor="rgba(255, 99, 132, 0.2)" />
                </linearGradient>
                <filter id="whyTriGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {Array.from({ length: 6 }).map((_, i) => {
                const pos = WHY_TRI_POSITIONS[i] ?? { x: 200 + (i % 3) * 300, y: 200 + Math.floor(i / 3) * 300 };
                const size = 60 + (i % 3) * 20;
                const totalRotation = getWhyTriangleRotation(i);
                return (
                  <g key={i} transform={`translate(${pos.x}, ${pos.y}) rotate(${totalRotation})`}>
                    <polygon
                      points={`0,${-size} ${size * 0.866},${size * 0.5} ${-size * 0.866},${size * 0.5}`}
                      fill="none"
                      stroke={`url(#whyTriGradient${(i % 3) + 1})`}
                      strokeWidth="1.5"
                      filter="url(#whyTriGlow)"
                      opacity="0.5"
                    />
                  </g>
                );
              })}
            </svg>
          </div>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <h2 className={`gpu-rental-why-title ${whyVisible ? 'visible' : ''}`}>
              {highlightLandingText(t('gpuRentalWhyTitle'), ['rent', 'GPU', 'server', 'арендовать', 'сервер'])}
            </h2>
            <div className="gpu-rental-why-items">
              <div className="gpu-rental-why-item" ref={el => { whyItemRefs.current[0] = el; }}>
                <h3 className="gpu-rental-why-item-title">{t('gpuRentalWhy1Title')}</h3>
                <p className="gpu-rental-why-item-desc">{t('gpuRentalWhy1Desc')}</p>
              </div>
              <div className="gpu-rental-why-divider-wrapper" ref={el => { whyDividerRefs.current[0] = el; }}>
                <div className="gpu-rental-why-divider gpu-rental-why-divider-title" />
                <div className="gpu-rental-why-divider gpu-rental-why-divider-desc" />
              </div>
              <div className="gpu-rental-why-item" ref={el => { whyItemRefs.current[1] = el; }}>
                <h3 className="gpu-rental-why-item-title">{t('gpuRentalWhy2Title')}</h3>
                <p className="gpu-rental-why-item-desc">{t('gpuRentalWhy2Desc')}</p>
              </div>
              <div className="gpu-rental-why-divider-wrapper" ref={el => { whyDividerRefs.current[1] = el; }}>
                <div className="gpu-rental-why-divider gpu-rental-why-divider-title" />
                <div className="gpu-rental-why-divider gpu-rental-why-divider-desc" />
              </div>
              <div className="gpu-rental-why-item" ref={el => { whyItemRefs.current[2] = el; }}>
                <h3 className="gpu-rental-why-item-title">{t('gpuRentalWhy3Title')}</h3>
                <p className="gpu-rental-why-item-desc">{t('gpuRentalWhy3Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="gpu-rental-how-to">
          <div className="container">
            <h2 className="gpu-rental-how-to-title">
              {highlightLandingText(t('gpuRentalHowToTitle'), ['GPU', 'server', 'сервер'])}
            </h2>
            <div className="gpu-rental-how-to-grid">
              <div className="gpu-rental-how-to-card">
                <div className="gpu-rental-how-to-icon" aria-hidden="true">
                  <span>1</span>
                </div>
                <h3 className="gpu-rental-how-to-card-title">{t('gpuRentalHowTo1Title')}</h3>
                <p className="gpu-rental-how-to-card-desc">{t('gpuRentalHowTo1Desc')}</p>
              </div>
              <div className="gpu-rental-how-to-card">
                <div className="gpu-rental-how-to-icon" aria-hidden="true">
                  <span>2</span>
                </div>
                <h3 className="gpu-rental-how-to-card-title">{t('gpuRentalHowTo2Title')}</h3>
                <p className="gpu-rental-how-to-card-desc">{t('gpuRentalHowTo2Desc')}</p>
              </div>
              <div className="gpu-rental-how-to-card">
                <div className="gpu-rental-how-to-icon" aria-hidden="true">
                  <span>3</span>
                </div>
                <h3 className="gpu-rental-how-to-card-title">{t('gpuRentalHowTo3Title')}</h3>
                <p className="gpu-rental-how-to-card-desc">{t('gpuRentalHowTo3Desc')}</p>
              </div>
            </div>
            <div className="gpu-rental-how-to-cta">
              <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                {t('getAccess')}
              </button>
            </div>
          </div>
        </section>
      </div>
      </div>

      <LandingFooter
        t={t}
        language={language}
        onToggleLanguage={toggleLanguage}
        isLightTheme={!isDark}
        onToggleTheme={() => setIsDark(!isDark)}
        onGetAccess={() => setIsAccessOpen(true)}
      />

      <GetAccessModal lang={lang} isOpen={isAccessOpen} onClose={() => setIsAccessOpen(false)} />
    </div>
  );
};

export default GpuRentalLandingPage;

